import { useState } from 'react';
import dayjs from 'dayjs';
import { DropdownField, InlineNotification, Modal, SpinningLoader, useFormField } from '@frontend/design-system';
import { useUpdateBusinessPlan, useBusinessPlans, type TrueLarkDomains } from '../index';
import { truelarkStyles as styles } from '../styles';

type Props = {
  domain: TrueLarkDomains;
  client_email: string;
  currentPlan: string | '';
  updatedBy: string;
  onClose: () => void;
  onSuccess: () => void;
};

export function UpdateBusinessPlanModal({ domain, client_email, currentPlan, updatedBy, onClose, onSuccess }: Props) {
  const { data, isLoading } = useBusinessPlans(domain);
  const statusFieldProps = useFormField({ type: 'dropdown', value: currentPlan }, [currentPlan]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mutation = useUpdateBusinessPlan({
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: () => setErrorMessage('Failed to update business plan. Please try again.'),
  });

  const handleUpdate = () => {
    if (!statusFieldProps.value) return;
    mutation.mutate({
      client_email: client_email,
      plan_id: statusFieldProps.value as string,
      date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      updated_by: updatedBy,
    });
  };

  return (
    <>
      <Modal.Header onClose={onClose}>Update Business Plan</Modal.Header>
      <Modal.Body>
        {errorMessage && <InlineNotification status='error' message={errorMessage} />}
        {isLoading ? (
          <SpinningLoader size='small' />
        ) : (
          <div css={styles.modalBody}>
            <DropdownField name='subscription_status' label='New Status' {...statusFieldProps}>
              {data?.plans.map(({ id, name }) => (
                <DropdownField.Option key={id} value={id}>
                  {id} - {name}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: 'Update',
          onClick: handleUpdate,
          disabled: !statusFieldProps.value,
          loading: isLoading,
        }}
        secondary={{ label: 'Cancel', onClick: onClose }}
      />
    </>
  );
}
